import { selectedFeatureAtom } from '@/atoms/selection.atom';
import { addPointModeAtom } from '@/atoms/viewer.atom';
import { Check, Trash } from '@mynaui/icons-react';
import { useAtom, useAtomValue, useSetAtom } from 'jotai';
import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import {
  FloatingToolbar,
  FloatingToolbarButton,
  FloatingToolbarSeparator,
} from './FloatingToolbar';

/** Skip keyboard shortcuts when the user is typing in a form field. */
const isTypingTarget = (e: KeyboardEvent) => {
  const t = e.target as HTMLElement | null;
  if (!t) return false;
  return (
    t.tagName === 'INPUT' ||
    t.tagName === 'TEXTAREA' ||
    t.tagName === 'SELECT' ||
    t.isContentEditable
  );
};

/**
 * Top-middle floating toolbar for the Add Point tool. Replaces the
 * bottom-right drawer footer that previously housed the Save / Cancel
 * buttons for draft points — keeps every creation tool's commit chrome in
 * the same spot.
 *
 * Cancel resets `addPointModeAtom` so the layer-row "+" trigger clears its
 * active tint, and clears `selectedFeatureAtom` so the in-scene draft
 * sphere disappears immediately.
 *
 * OK is disabled until a draft point exists (i.e. the user has clicked
 * somewhere on the point cloud / mesh).
 */
export const AddPointToolbar = () => {
  const [mode, setMode] = useAtom(addPointModeAtom);
  const selectedFeature = useAtomValue(selectedFeatureAtom);
  const setSelectedFeature = useSetAtom(selectedFeatureAtom);
  const { t } = useTranslation('components');

  const hasDraft = !!selectedFeature?.isDraft;

  // Esc → cancel, Enter → OK (when a draft exists). Listened at window
  // level so the canvas doesn't need focus.
  useEffect(() => {
    if (!mode.enabled) return;
    const onKey = (e: KeyboardEvent) => {
      if (isTypingTarget(e)) return;
      if (e.key === 'Escape') {
        e.preventDefault();
        setSelectedFeature(null);
        setMode({ enabled: false, targetLayerId: null, draftFeature: null });
      } else if (e.key === 'Enter' && hasDraft && selectedFeature?.onSave) {
        e.preventDefault();
        selectedFeature.onSave(selectedFeature);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [mode.enabled, hasDraft, selectedFeature, setMode, setSelectedFeature]);

  if (!mode.enabled) return null;

  const status = hasDraft
    ? t('addPointTool.adjusting')
    : t('addPointTool.pickFirstPoint');

  const handleCancel = () => {
    setSelectedFeature(null);
    setMode({ enabled: false, targetLayerId: null, draftFeature: null });
  };

  const handleOk = () => {
    if (!selectedFeature?.onSave) return;
    selectedFeature.onSave(selectedFeature);
  };

  return (
    <FloatingToolbar status={status}>
      <FloatingToolbarButton
        icon={<Check />}
        tooltip={t('addPointTool.ok')}
        disabled={!hasDraft}
        intent='primary'
        primaryClass='bg-orange-500 hover:bg-orange-600'
        onClick={handleOk}
      />
      <FloatingToolbarSeparator />
      <FloatingToolbarButton
        icon={<Trash />}
        tooltip={t('addPointTool.cancel')}
        intent='danger'
        onClick={handleCancel}
      />
    </FloatingToolbar>
  );
};
